import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge } from "../components/shared";
import { formatINR, formatDate } from "../lib/format";
import { TXN_TYPE_META } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/approvals").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try { await api.post(`/transactions/${id}/approve`); toast.success("Approved & posted"); load(); window.dispatchEvent(new CustomEvent("prabhat:refresh")); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doReject = async () => {
    try { await api.post(`/transactions/${reject.id}/reject`, { reason }); toast.success("Rejected"); setReject(null); setReason(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Approvals" subtitle={`${items.length} transaction(s) awaiting approval`} />
      {loading ? <Loading /> : items.length === 0 ? <EmptyState title="Nothing to approve" sub="Transactions above the approval threshold appear here." /> : (
        <TableWrap>
          <thead><tr><Th>Txn</Th><Th>Date</Th><Th>Type</Th><Th>Party</Th><Th>Description</Th><Th className="text-right">Amount</Th><Th>By</Th><Th>Action</Th></tr></thead>
          <tbody>{items.map((t) => (
            <tr key={t.id} data-testid={`approval-row-${t.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
              <Td className="font-mono">{t.txn_number}</Td><Td>{formatDate(t.txn_date)}</Td>
              <Td><Badge cls={TXN_TYPE_META[t.type]?.cls}>{TXN_TYPE_META[t.type]?.label || t.type}</Badge></Td>
              <Td>{t.candidate_name || t.payee_name || "-"}</Td><Td>{t.description || "-"}</Td>
              <Td className="text-right font-mono font-semibold">{formatINR(t.amount)}</Td>
              <Td className="text-slate-400">{t.created_by_name}</Td>
              <Td><div className="flex gap-1.5">
                <Button size="sm" onClick={() => approve(t.id)} data-testid={`approve-${t.id}`} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 gap-1"><Check className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" onClick={() => setReject(t)} data-testid={`reject-${t.id}`} className="h-7 px-2 text-rose-600 border-rose-200 gap-1"><X className="h-3.5 w-3.5" /></Button>
              </div></Td>
            </tr>
          ))}</tbody>
        </TableWrap>
      )}
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent><DialogHeader><DialogTitle>Reject {reject?.txn_number}</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection (required)" data-testid="reject-reason" />
          <DialogFooter><Button variant="outline" onClick={() => setReject(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!reason} onClick={doReject} data-testid="reject-confirm">Reject</Button></DialogFooter>
        </DialogContent></Dialog>
    </div>
  );
}
